import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Award, ShieldCheck, FileCheck2, BadgeCheck, ArrowRight } from "lucide-react";
import CTABanner from "@/components/site/CTABanner";

const certs = [
  { icon: Award, label: "ISO-Aligned Quality Processes" },
  { icon: ShieldCheck, label: "ASTM Material Standards" },
  { icon: FileCheck2, label: "AASHTO Design Compliance" },
  { icon: BadgeCheck, label: "DPWH Specification Support" },
];

export default function About() {
  return (
    <>
      <section className="bg-[#0A0A0A] pt-40 pb-28 clip-diagonal-bottom relative overflow-hidden">
        <div className="absolute top-0 right-0 h-full w-40 bg-[#E4001C]/70 hidden md:block" style={{ clipPath: "polygon(60% 0, 100% 0, 40% 100%, 0 100%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <p className="font-tech font-semibold uppercase tracking-[0.25em] text-[#E4001C] text-sm mb-4">About Seisfra</p>
          <h1 className="font-heading font-extrabold uppercase text-white text-5xl md:text-7xl tracking-wide max-w-3xl">
            The Engineering Behind the Gap
          </h1>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="font-tech font-semibold uppercase tracking-[0.2em] text-[#E4001C] text-sm mb-3">Our Story</p>
            <h2 className="font-heading font-extrabold uppercase text-4xl tracking-wide mb-6 text-[#171717]">Precision Is Our Foundation</h2>
            <p className="text-[#171717]/70 leading-relaxed mb-5">
              Seisfra was founded on a simple conviction: the movement joints in a structure
              deserve the same engineering rigor as its beams and columns. In a country that
              sits on the Pacific Ring of Fire, that conviction is not optional — it is essential.
            </p>
            <p className="text-[#171717]/70 leading-relaxed">
              Today, Seisfra designs and supplies expansion joint systems for buildings, bridges,
              and infrastructure projects nationwide, partnering with contractors, architects, and
              engineers who refuse to compromise on long-term structural performance.
            </p>
          </div>
          <div className="relative">
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#E4001C]" />
            <img src="https://media.base44.com/images/public/user_6962101245ec965f972f6d3a/7950e20d6_BgSeisfra.png"
              alt="Seisfra brand banner with bridge and expansion joint" className="relative w-full object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white p-10 border-t-4 border-[#E4001C]">
            <Target className="w-10 h-10 text-[#E4001C] mb-5" />
            <h3 className="font-heading font-extrabold uppercase text-3xl tracking-wide mb-4 text-[#171717]">Mission</h3>
            <p className="text-[#171717]/70 leading-relaxed">
              To protect Philippine structures and the people who use them by delivering
              precision-engineered movement joint systems, backed by honest technical guidance
              and dependable nationwide supply.
            </p>
          </div>
          <div className="bg-[#0A0A0A] p-10 border-t-4 border-[#E4001C]">
            <Eye className="w-10 h-10 text-[#E4001C] mb-5" />
            <h3 className="font-heading font-extrabold uppercase text-3xl tracking-wide mb-4 text-white">Vision</h3>
            <p className="text-[#A8ADB4] leading-relaxed">
              To be the definitive authority on expansion joint systems in the Philippines —
              the first name specified on every building, bridge, and infrastructure project
              that moves.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="font-tech font-semibold uppercase tracking-[0.2em] text-[#E4001C] text-sm mb-3">Standards</p>
          <h2 className="font-heading font-extrabold uppercase text-4xl tracking-wide mb-12 text-[#171717]">Certifications & Compliance</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {certs.map((c) => (
              <div key={c.label} className="border border-gray-200 p-8 text-center hover:border-[#E4001C]/50 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <c.icon className="w-10 h-10 text-[#0A0A0A] mx-auto mb-4" />
                <p className="font-tech font-semibold uppercase tracking-wide text-sm text-[#171717]">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <img src="https://media.base44.com/images/public/6a45c3b894a45b7fc0b9be49/04adc74dd_generated_1acb8efa.png" alt="Seisfra fabrication and supply facility"
            className="w-full aspect-[16/10] object-cover" />
          <div>
            <p className="font-tech font-semibold uppercase tracking-[0.2em] text-[#E4001C] text-sm mb-3">Capability</p>
            <h2 className="font-heading font-extrabold uppercase text-4xl tracking-wide mb-6 text-[#171717]">Facilities & Team</h2>
            <p className="text-[#171717]/70 leading-relaxed mb-8">
              Our supply and technical operations are supported by a team of engineers and
              project specialists who review every RFQ against real movement requirements —
              thermal, seismic, and structural — before a single component is quoted.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#E4001C] text-white font-tech font-semibold uppercase tracking-widest px-6 py-3.5 transition-colors">
                View Products <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-[#0A0A0A] hover:border-[#E4001C] hover:text-[#E4001C] font-tech font-semibold uppercase tracking-widest px-6 py-3.5 transition-colors">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}